import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { DexService } from '../services/DexService';
import { DexServiceConfig, LogLevel, LogData } from '../types/dex';

// 创建日志的参数
interface CreateLogParams {
  level: LogLevel;
  message: string;
  walletIndex?: number | null;
  data?: any;
}

// 创建日志的辅助函数
const createLog = (params: CreateLogParams): LogData => ({
  level: params.level,
  message: params.message,
  walletIndex: params.walletIndex,
  data: params.data,
  timestamp: Date.now()
});

// DexService上下文的接口定义
interface DexServiceContextType {
  dexService: DexService | null;
  config: DexServiceConfig | null;
  logs: LogData[];
  addLog: (params: CreateLogParams) => void;
  clearLogs: () => void;
  initialize: (newConfig: DexServiceConfig) => Promise<void>;
  isInitialized: boolean;
  resetDexService: () => void;
  setDexServiceConfig: (newConfig: DexServiceConfig) => void;
}

// 创建上下文
const DexServiceContext = createContext<DexServiceContextType>({
  dexService: null,
  config: null,
  logs: [],
  addLog: () => {},
  clearLogs: () => {},
  initialize: async () => {},
  isInitialized: false,
  resetDexService: () => {},
  setDexServiceConfig: () => {}
});

// 上下文提供者的属性
interface DexServiceProviderProps {
  children: ReactNode;
}

// 上下文提供者组件
export const DexServiceProvider: React.FC<DexServiceProviderProps> = ({ children }) => {
  const [dexService, setDexService] = useState<DexService | null>(null);
  const [config, setConfig] = useState<DexServiceConfig | null>(null);
  const [logs, setLogs] = useState<LogData[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // 添加日志
  const addLog = useCallback((params: CreateLogParams) => {
    const log = createLog(params);
    console.log(`[Date: ${new Date().toLocaleString()}] ${log.level}: ${log.message}`, (log.data));
    setLogs(prevLogs => [...prevLogs, log]);
  }, []);

  // 清空日志
  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // 处理日志
  const handleLog = useCallback((log: LogData) => {
    setLogs(prevLogs => [...prevLogs, log]);
  }, []);

  // 重置DexService
  const resetDexService = useCallback(() => {
    setDexService(null);
    setConfig(null);
    setIsInitialized(false);
    setLogs([]);
  }, []);

  // 初始化DexService
  const initialize = useCallback(async (newConfig: DexServiceConfig) => {
    try {
      // 创建新的DexService实例
      const newDexService = new DexService({
        ...newConfig,
        onLog: handleLog
      });
      

      console.log("上下文调用初始化", newConfig);
      // 初始化
      await newDexService.initialize([...newConfig.wallets]);
      
      // 更新状态
      setDexService(newDexService);
      setConfig(newConfig);
      setIsInitialized(true);
      
      // 清空日志
      clearLogs();
      
      // 添加初始化成功日志
      handleLog({
        level: LogLevel.INFO,
        message: `DexService初始化成功: ${newConfig.dex?.router?.name} on ${newConfig.rpc.name}`,
        data: {
          router: newConfig.dex?.router?.name,
          network: newConfig.rpc.name,
          chainId: newConfig.rpc.chainId,
          walletCount: newConfig.wallets.length
        },
        timestamp: Date.now()
      });
    } catch (error: any) {
      // 添加错误日志
      handleLog({
        level: LogLevel.ERROR,
        message: `DexService初始化失败: ${error.message}`,
        data: { error },
        timestamp: Date.now()
      });
      
      // 重置状态
      setDexService(null);
      setConfig(null);
      setIsInitialized(false);
      
      throw error;
    }
  }, [handleLog, clearLogs]);

  // 组件挂载时加载配置
  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem('dexServiceConfig');
      if (savedConfig) {
        const parsedConfig: DexServiceConfig = JSON.parse(savedConfig);
        // 只更新配置状态，不初始化服务
        setConfig(parsedConfig);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
    }
  }, []);

  // 提供上下文值
  const contextValue: DexServiceContextType = {
    dexService,
    config,
    logs,
    addLog,
    clearLogs,
    initialize,
    isInitialized,
    resetDexService,
    setDexServiceConfig: setConfig
  };

  return (
    <DexServiceContext.Provider value={contextValue}>
      {children}
    </DexServiceContext.Provider>
  );
};

// 自定义Hook
export const useDexService = () => {
  const context = useContext(DexServiceContext);
  if (!context) {
    throw new Error('useDexService必须在DexServiceProvider内使用');
  }
  return context;
};
